import { selectStandardProps } from "@/utils/componentUtils";
import { Form, Select } from "antd";
import { FormItemProps } from "antd/lib/form";
import { SelectProps } from "antd/lib/select";
import { t } from "i18next";
import groupBy from "lodash/groupBy";
import { useTranslation } from "react-i18next";
import { DEFAULT_LOCALE } from "../../../../app/i18n";
import { useEnumeration } from "../../../../common/api/queries";
import { InstitutionType } from "../../../institution/enums";
import { InstitutionBase } from "../../../institution/types";
import { EnumerationType } from "../../enums";
import { FilterFunction, InstitutionEnumeration } from "../../types";

const formatInstitutionName = (institution?: InstitutionEnumeration) => {
  return institution
    ? institution.deactivated
      ? `${institution.name} (${t("institution.helpers.deactivated")})`
      : institution.name
    : undefined;
};

interface Props {
  formItemProps: FormItemProps;
  selectProps?: SelectProps<string>;
  optionsProps?: InstitutionOptionsProps;
}

interface InstitutionOptionsProps {
  hideAll?: boolean;
  includeDeactivated?: boolean;
  selected?: InstitutionBase[];
  filterType?: InstitutionType;
  optGroups?: InstitutionOptGroupProps[];
  groupByType?: boolean;
  filter?: FilterFunction<InstitutionEnumeration>;
}

interface InstitutionOptGroupProps {
  label: string;
  filter: FilterFunction<InstitutionEnumeration>;
}

const InstitutionSelect = ({ formItemProps, selectProps, optionsProps }: Props) => {
  const { t } = useTranslation();

  const institutionsEnums = useEnumeration(EnumerationType.INSTITUTIONS);

  const resolveInstitutionSelectOptions = () => {
    if (optionsProps?.hideAll) {
      return [];
    }

    let options = [...institutionsEnums];

    if (optionsProps) {
      options = options.filter((institution) => optionsProps.includeDeactivated || !institution.deactivated);

      if (optionsProps.filterType) {
        options = options.filter((institution) => institution.type === optionsProps.filterType);
      }

      if (optionsProps.filter) {
        options = options.filter(optionsProps.filter);
      }

      if (optionsProps.selected) {
        optionsProps.selected
          .filter((selectedInstitution) =>
            optionsProps.filterType ? selectedInstitution.type === optionsProps.filterType : true,
          )
          .forEach((selectedInstitution) => {
            if (options.some((option) => option.id === selectedInstitution.id)) {
              return;
            }

            const institutionWithSettings = institutionsEnums.find(
              (institution) => institution.id === selectedInstitution.id,
            );

            if (institutionWithSettings) {
              options.unshift(institutionWithSettings);
            }
          });
      }
    } else {
      options = options.filter((institution) => !institution.deactivated);
    }

    return options.sort((a, b) => a.name.localeCompare(b.name, DEFAULT_LOCALE, { sensitivity: "accent" }));
  };

  const resolveInstitutionSelectGroupOptions = (filterOptions: FilterFunction<InstitutionEnumeration>) => {
    let options = [...institutionsEnums];
    if (!optionsProps?.includeDeactivated) {
      options = options.filter((institution) => !institution.deactivated);
    }
    return options
      .filter(filterOptions)
      .sort((a, b) => a.name.localeCompare(b.name, DEFAULT_LOCALE, { sensitivity: "accent" }));
  };

  if (optionsProps?.optGroups) {
    return (
      <Form.Item {...formItemProps}>
        <Select
          {...selectStandardProps}
          {...selectProps}
          options={optionsProps.optGroups.map((group) => ({
            label: group.label,
            options: resolveInstitutionSelectGroupOptions(group.filter).map((i) => ({
              value: i.id,
              label: formatInstitutionName(i),
            })),
          }))}
        />
      </Form.Item>
    );
  } else if (optionsProps?.groupByType) {
    const institutionsGroups = groupBy(resolveInstitutionSelectOptions(), (institution) => institution.type);
    return (
      <Form.Item {...formItemProps}>
        <Select
          {...selectStandardProps}
          {...selectProps}
          options={Object.keys(institutionsGroups)
            .sort((a, b) => Object.keys(InstitutionType).indexOf(a) - Object.keys(InstitutionType).indexOf(b))
            .map((type) => ({
              label: t("institution.helpers.optGroup." + type),
              options: institutionsGroups[type]?.map((i) => ({ value: i.id, label: formatInstitutionName(i) })) ?? [],
            }))}
        />
      </Form.Item>
    );
  }

  return (
    <Form.Item {...formItemProps}>
      <Select
        {...selectStandardProps}
        {...selectProps}
        options={resolveInstitutionSelectOptions().map((i) => ({ value: i.id, label: formatInstitutionName(i) }))}
      />
    </Form.Item>
  );
};

export default InstitutionSelect;
